import { Section } from './Section';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import {
  SupplementalKnowledgeAiReply,
  SupplementKnowledgeAnswer,
} from 'services/api/supplementKnowledge';
import botAvatarPng from 'views/Knowledge/assets/botAvatar.png';
import { getTranslateContent } from '../utils';
import { Divider, Empty, Tooltip } from 'antd';
import { ImageAnswer, VideoAnswer } from '../AnswerCell';
import { useQuery } from '@tanstack/react-query';
import emptySvg from 'views/Knowledge/assets/empty.svg';
import { css, cx } from '@emotion/css';
import { useEffect, useMemo } from 'react';
import { eventBus } from 'services/eventBus';
import { useSafeState } from 'ahooks';
import { queries } from 'services/queries';

export interface PureAIReplyProps {
  text: string;
  textTranslate?: string;
  time?: number;
  mediaExtra?: React.ReactNode;
  locate?: boolean;
  onLocate?: () => void;
  flashKey?: number;
}

export const PureAIReplyMediaContent = (props: { replyContent: SupplementKnowledgeAnswer[] }) => {
  const { replyContent } = props;

  return (
    <div className="flex items-center gap-2">
      {replyContent?.map((answer) => {
        if (answer.msgType === 'IMAGE') {
          return (
            <ImageAnswer
              answer={answer}
              ellipsis={false}
              textClassName="!text-xs !text-[#84868C]"
            />
          );
        }
        if (answer.msgType === 'VIDEO') {
          return (
            <VideoAnswer
              answer={answer}
              ellipsis={false}
              textClassName="!text-xs !text-[#84868C]"
            />
          );
        }
        return null;
      })}
    </div>
  );
};

export const PureAIReplyContent = (props: PureAIReplyProps) => {
  const { text, textTranslate, mediaExtra, locate = false, onLocate, flashKey = 0 } = props;
  const { t } = useTranslate();
  const systemLanguage = useSystemLanguageValue();

  const [flash, setFlash] = useSafeState(false);

  const { data } = useQuery({
    enabled: !!text && !textTranslate,
    ...queries.translates.overseaTranslate(text, systemLanguage, 'supplementKnowledge'),
  });
  const translateContent = textTranslate || data;

  const content = useMemo(() => {
    const content = (
      <div
        className={cx('flex-1 bg-[#D0E4FF] rounded-sm px-[10px] py-2', {
          'cursor-pointer': locate,
          [flashCss]: flash,
        })}
        onClick={() => {
          if (locate) {
            onLocate?.();
          }
        }}
      >
        <div className="text-xs text-[#404040] whitespace-pre-wrap">{text}</div>
        {!!translateContent && (
          <>
            <Divider className="!my-2" />
            <div className="text-xs text-[#84868C] whitespace-pre-wrap">{translateContent}</div>
          </>
        )}
        {mediaExtra}
      </div>
    );

    if (locate) {
      return (
        <Tooltip title={t('定位至答案')} placement="bottom">
          {content}
        </Tooltip>
      );
    }
    return content;
  }, [locate, text, translateContent, mediaExtra, onLocate, t, flash]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (flashKey) {
      setFlash(true);
      timeoutId = setTimeout(() => {
        setFlash(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [flashKey, setFlash]);

  return (
    <div className="flex gap-2">
      <img className="w-[32px] h-[32px]" src={botAvatarPng} alt="botAvatar" />
      {content}
    </div>
  );
};

export const PureAIReply = (props: PureAIReplyProps) => {
  const { text, time } = props;
  const { t } = useTranslate();

  const hasText = !!text;

  return (
    <Section
      title={t('当前AI回复')}
      extra={
        time ? (
          <span className="text-xs text-[#777777]">
            {t('回复生成时间', {
              time: dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
            })}
          </span>
        ) : null
      }
    >
      {hasText ? (
        <PureAIReplyContent {...props} />
      ) : (
        <Empty
          className={cx(
            'text-[#777777]',
            css`
              .ant-empty-image {
                height: 72px;
              }
            `,
          )}
          image={emptySvg}
          description={t('no-ai-reply-knowledge-tips')}
        />
      )}
    </Section>
  );
};

interface AIReplyProps {
  aiReply: SupplementalKnowledgeAiReply;
  locate?: boolean;
  flashKey?: number;
}

export const AIReply = (props: AIReplyProps) => {
  const { aiReply, locate = false, flashKey } = props;
  const language = useSystemLanguageValue();

  const { replyContent, replyTime } = aiReply;

  const textAnswer = replyContent?.find((answer) => answer.msgType === 'TEXT');
  const textAnswerTranslate = getTranslateContent(language, textAnswer?.translateMap);

  return (
    <PureAIReply
      text={textAnswer?.message || ''}
      textTranslate={textAnswerTranslate}
      time={replyTime}
      mediaExtra={<PureAIReplyMediaContent replyContent={replyContent} />}
      locate={locate && !!textAnswer?.messageId}
      onLocate={() => {
        if (textAnswer?.messageId) {
          eventBus.emit('SCROLL_TO_MSG_BY_ID', textAnswer.messageId);
        }
      }}
      flashKey={flashKey}
    />
  );
};

const flashCss = css`
  animation: flash 2s ease-in-out infinite;

  @keyframes flash {
    50% {
      opacity: 0.5;
    }
  }
`;
